import { useQuery } from "@apollo/client";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import Spinner from "components/base/Spinner";
import { useModals } from "ModalProvider";
import { forwardRef, useImperativeHandle, useState } from "react";
import { RiArrowUpDownLine } from "react-icons/ri";
import { searchByProp } from "utils/search";
import XLSX from "xlsx";
import { ODOO_MARGIN_BASE_ON_INVOICES } from "./graphql";
import InvoiceMarginDetailView from "./InvoiceMarginDetailView";
// import InvoiceMarginDetailView from "./InvoiceMarginDetailView";

const OdooMarginTableView = forwardRef(
  ({ searchText, startDate, endDate }, ref) => {
    console.log("startDate", startDate);

    const { loading, error, data } = useQuery(ODOO_MARGIN_BASE_ON_INVOICES, {
      variables: { startDate, endDate, partnerIds: [] },
    });

    if (loading) return <Spinner />;
    if (error) return <Errors error={error} />;

    const res = JSON.parse(data.odooMarginBaseOnInvoices);

    console.log("res", res);

    const invoices = res.filter((i) =>
      searchByProp(i, ["name", "partner_name", "ref"], searchText),
    );

    return (
      <TableWithMargins
        ref={ref}
        invoices={invoices}
        startDate={startDate}
        endDate={endDate}
      />
    );
  },
);

const TableWithMargins = forwardRef(({ startDate, endDate, invoices }, ref) => {
  const modal = useModals();
  // Default sort by date, support sort by sales and margin.
  const [sortBy, setSortBy] = useState("date");

  const finalInvoices = invoices.sort((a, b) => {
    const sortOptions = {
      date: new Date(a.date) - new Date(b.date),
      "-date": new Date(b.date) - new Date(a.date),
      partner: a.partner_name.localeCompare(b.partner_name),
      "-partner": b.partner_name.localeCompare(a.partner_name),
      sales: b.total_sales - a.total_sales,
      "-sales": a.total_sales - b.total_sales,
      margin: b.margin - a.margin,
      "-margin": a.margin - b.margin,
    };
    return sortOptions[sortBy];
  });

  const totalSales = finalInvoices.reduce(
    (acc, i) => acc + (i.total_sales ?? 0),
    0,
  );

  const totalCredit = finalInvoices.reduce(
    (acc, i) => acc + (i.credit_total ?? 0),
    0,
  );
  const totalDebit = finalInvoices.reduce(
    (acc, i) => acc + (i.debit_total ?? 0),
    0,
  );
  const totalDividedBy = finalInvoices.reduce(
    (acc, i) => acc + (i.divided_by ?? 1),
    0,
  );
  const totalMargin = ((totalCredit - totalDebit) / totalDividedBy) * 100;

  function showInvoice(invoice) {
    modal.present({
      title: `Invoice ${invoice.name}`,
      subtitle: invoice.partner_name,
      children: <InvoiceMarginDetailView invoice={invoice} />,
    });
  }

  useImperativeHandle(ref, () => ({
    download() {
      const excelData = finalInvoices.map((i) => ({
        Date: i.date,
        Number: i.name,
        Partner: i.partner_name,
        Reference: i.ref,
        Type: i.type_name,
        "Sales Total": i.total_sales.toFixed(2),
        "Margin (%)": i.margin.toFixed(2),
      }));
      const sheet = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, sheet);
      const filename = `odoo_invoice_margin_${startDate}_${endDate}.xlsx`;

      XLSX.writeFile(wb, filename);
    },
  }));

  return (
    <table className=" whitespace-nowrap font-code">
      <thead>
        <tr className="sticky top-0 bg-gray-50 dark:bg-gray-800">
          <th className="py-3 px-6">
            <div className="flex items-center space-x-2">
              <span>Date</span>
              <Button
                onClick={() => {
                  setSortBy(sortBy === "date" ? "-date" : "date");
                }}
              >
                <RiArrowUpDownLine />
              </Button>
            </div>
          </th>
          <th className="px-6">Number</th>
          <th className="px-6">
            <div className="flex items-center space-x-2">
              <span>Partner</span>
              <Button
                onClick={() => {
                  setSortBy(sortBy === "partner" ? "-partner" : "partner");
                }}
              >
                <RiArrowUpDownLine />
              </Button>
            </div>
          </th>
          <th className="px-6">Reference</th>
          <th className="px-6 text-right">Type</th>
          <th className="px-6 text-right">
            <div className="flex justify-end items-center space-x-2">
              <Button
                onClick={() => {
                  setSortBy(sortBy === "sales" ? "-sales" : "sales");
                }}
              >
                <RiArrowUpDownLine />
              </Button>
              <span>Sales Total</span>
            </div>
          </th>
          <th className="px-6 text-right">
            <div className="flex justify-end items-center space-x-2">
              <div className="flex justify-end items-center space-x-2">
                <Button
                  onClick={() => {
                    setSortBy(sortBy === "margin" ? "-margin" : "margin");
                  }}
                >
                  <RiArrowUpDownLine />
                </Button>
                <span>Margin</span>
              </div>
            </div>
          </th>
        </tr>
      </thead>
      <tbody className=" divide-y dark:divide-gray-700">
        {finalInvoices.map((invoice, index) => (
          <tr
            key={index}
            className=" hover:bg-blue-50 dark:hover:bg-blue-900 cursor-pointer"
            onClick={() => showInvoice(invoice)}
          >
            <td className="px-6">{invoice.date}</td>
            <td className="px-6">{invoice.name}</td>
            <td className="px-6 whitespace-normal">{invoice.partner_name}</td>
            <td className="px-6 whitespace-normal">{invoice.ref}</td>
            <td className="px-6 text-right">{invoice.type_name}</td>
            <td className="px-6 text-right">
              {parseFloat(invoice.total_sales?.toFixed(2)).toLocaleString()}
            </td>
            <td className="px-6 text-right">{invoice.margin?.toFixed(2)}%</td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr className="sticky bottom-0 z-10 bg-fuchsia-50 text-base dark:bg-gray-800 font-semibold">
          <td className="px-6 py-3">Total</td>
          <td colSpan="4"></td>
          <td className="px-6 text-right">
            {parseFloat(totalSales.toFixed(2)).toLocaleString()}
          </td>
          <td className="px-6 text-right">{totalMargin.toFixed(2)}%</td>
        </tr>
      </tfoot>
    </table>
  );
});

export default OdooMarginTableView;
